import * as button from "./button"
import "d3-drag"
import * as params from "./params"

export type ResizeOffset = {
  top: number
  right: number
  bottom: number
  left: number
}

export const append = (buttonElement) => {
  createResizer(buttonElement, true)
    .classed("top", true)
    .classed("left", true)
    .style("cursor", "nw-resize")
  createResizer(buttonElement, false)
    .classed("top", true)
    .classed("center", true)
    .attr("x", (d) => d.width / 2)
    .style("cursor", "n-resize")
  createResizer(buttonElement, true)
    .classed("top", true)
    .classed("right", true)
    .attr("x", (d) => d.width)
    .attr("transform", "translate(-20, 0)")
    .style("cursor", "ne-resize")
  createResizer(buttonElement, false)
    .classed("right", true)
    .classed("middle", true)
    .attr("x", (d) => d.width)
    .attr("y", (d) => d.height / 2)
    .attr("transform", "translate(-20,-10)")
    .style("cursor", "e-resize")
  createResizer(buttonElement, true)
    .classed("bottom", true)
    .classed("right", true)
    .attr("x", (d) => d.width)
    .attr("y", (d) => d.height)
    .attr("transform", "translate(-20,-20)")
    .style("cursor", "se-resize")
  createResizer(buttonElement, false)
    .classed("bottom", true)
    .classed("center", true)
    .attr("x", (d) => d.width / 2)
    .attr("y", (d) => d.height)
    .attr("transform", "translate(-10,-20)")
    .style("cursor", "s-resize")
  createResizer(buttonElement, true)
    .classed("bottom", true)
    .classed("left", true)
    .attr("y", (d) => d.height)
    .attr("transform", "translate(0,-20)")
    .style("cursor", "sw-resize")
  createResizer(buttonElement, false)
    .classed("left", true)
    .classed("middle", true)
    .attr("y", (d) => d.height / 2)
    .attr("transform", "translate(0,-10)")
    .style("cursor", "w-resize")

  computeAttributes(buttonElement)
}

export const computeAttributes = (buttonElement) => {
  buttonElement
    .select(".top.left rect")
    .style("stroke-dasharray", "0.00001,20,40,0")
    .style("transform", "translate(20px,20px) rotate(180deg)")
  buttonElement
    .select(".top.left")
    .select("path.arrow")
    .style("transform", "translate(10px,10px) rotate(90deg)")
  buttonElement
    .select(".top.center rect")
    .style("stroke-dasharray", "20,60")
    .style("stroke-dashoffset", "20")
    .style("transform", "translate(20px, 0px) rotate(90deg)")
  buttonElement
    .select(".top.center")
    .attr("transform", (d) => `translate(${d.width / 2 - 10},0)`)
    .select("path.arrow")
    .style("transform", "translate(10px,10px) rotate(135deg)")
  buttonElement
    .select(".top.right rect")
    .style("stroke-dasharray", "0.00001,20,40,0")
    .style("transform", "translate(0px,20px) rotate(-90deg)")
  buttonElement
    .select(".top.right")
    .attr("transform", (d) => `translate(${d.width - 20},0)`)
    .select("path.arrow")
    .style("transform", "translate(10px,10px) rotate(180deg)")
  buttonElement
    .select(".middle.right rect")
    .style("stroke-dasharray", "20,60")
    .style("stroke-dashoffset", "20")
    .style("transform", "translate(20px, 20px) rotate(180deg)")
  buttonElement
    .select(".middle.right")
    .attr("transform", (d) => `translate(${d.width - 20},${d.height / 2 - 10})`)
    .select("path.arrow")
    .style("transform", "translate(10px,10px) rotate(-135deg)")
  buttonElement.select(".bottom.right rect").style("stroke-dasharray", "0.00001,20,40,0")
  buttonElement
    .select(".bottom.right")
    .attr("transform", (d) => `translate(${d.width - 20},${d.height - 20})`)
    .select("path.arrow")
    .style("transform", "translate(10px,10px) rotate(-90deg)")
  buttonElement
    .select(".bottom.center rect")
    .style("stroke-dasharray", "20,60")
    .style("stroke-dashoffset", "20")
    .style("transform", "translate(0px, 20px) rotate(-90deg)")
  buttonElement
    .select(".bottom.center")
    .attr("transform", (d) => `translate(${d.width / 2 - 10},${d.height - 20})`)
    .select("path.arrow")
    .style("transform", "translate(10px,10px) rotate(-45deg)")
  buttonElement.select(".bottom.left rect").style("stroke-dasharray", "0.00001,40,0")
  buttonElement
    .select(".bottom.left")
    .attr("transform", (d) => `translate(0,${d.height - 20})`)
    .select("path.arrow")
    .style("transform", "translate(10px,10px)")
  buttonElement
    .select(".middle.left rect")
    .style("stroke-dasharray", "20,60")
    .style("stroke-dashoffset", "20")
  buttonElement
    .select(".middle.left")
    .attr("transform", (d) => `translate(0,${d.height / 2 - 10})`)
    .select("path.arrow")
    .style("transform", "translate(10px,10px) rotate(45deg)")

  // show only the corner resize handles if there is not enough space
  buttonElement.selectAll(".center").style("display", (d) => (d?.width >= 100 ? "inline" : "none"))
  buttonElement.selectAll(".middle").style("display", (d) => (d?.height >= 100 ? "inline" : "none"))
}

export type DragDirections = {
  top: boolean
  right: boolean
  bottom: boolean
  left: boolean
}

const createResizer = (buttonElement, isCorner) => {
  const resizerElement = buttonElement.append("g").classed("resizer", true)
  resizerElement
    .append("rect")
    .attr("width", 20)
    .attr("height", 20)
    .style("fill", "none")
    .style("stroke-width", 8)
    .style("stroke-opacity", isCorner ? 0.7 : 1)
  resizerElement.append("circle").attr("cx", 10).attr("cy", 10).attr("r", 20)
  resizerElement.append("path").classed("arrow", true).attr("d", "M -5 -5 l 0 10 l 10 0")
  return resizerElement
}

export const calculateResize = (datum, offset: ResizeOffset) => {
  const result: { x?: number; y?: number; width?: number; height?: number } = {}
  if (offset.left !== 0) {
    const rightEdge = datum.x + datum.width
    const maxX = rightEdge - params.minWidth
    result.x = Math.max(Math.min(maxX, datum.x - offset.left), 0)
    result.width = rightEdge - result.x
  } else if (offset.right !== 0) {
    result.width = Math.min(
      Math.max(params.minWidth, datum.width + offset.right),
      params.width - datum.x,
    )
  }
  if (offset.top !== 0) {
    const bottomEdge = datum.y + datum.height
    const maxY = bottomEdge - params.minHeight
    result.y = Math.max(Math.min(maxY, datum.y - offset.top), 0)
    result.height = bottomEdge - result.y
  } else if (offset.bottom !== 0) {
    result.height = Math.min(
      Math.max(params.minHeight, datum.height + offset.bottom),
      params.height - datum.y,
    )
  }
  return result
}

export const snapToGridAfterDrag = (buttonElement: any) => {
  const datum = buttonElement.datum()
  buttonElement.datum({
    ...datum,
    x: round(datum.x, params.horizontalResolution),
    y: round(datum.y, params.verticalResolution),
    width: round(datum.width, params.horizontalResolution),
    height: round(datum.height, params.verticalResolution),
  })
  button.updatePosition(buttonElement)

  return buttonElement.datum()
}

export const snapToGridAfterResize = (buttonElement: any, dragDirections: DragDirections) => {
  const datum = buttonElement.datum()
  const adjustedPosition = {
    x: round(datum.x, params.horizontalResolution),
    y: round(datum.y, params.verticalResolution),
    width: round(datum.width, params.horizontalResolution),
    height: round(datum.height, params.verticalResolution),
  }
  buttonElement.datum({
    ...datum,
    x: dragDirections.left ? adjustedPosition.x : datum.x,
    y: dragDirections.top ? adjustedPosition.y : datum.y,
    // if we are not dragging the right edge, the right edge (i.e. sum of x and width) should remain constant
    width: dragDirections.right
      ? adjustedPosition.width
      : datum.width + datum.x - adjustedPosition.x,
    // similarly for the bottom edge
    height: dragDirections.bottom
      ? adjustedPosition.height
      : datum.height + datum.y - adjustedPosition.y,
  })
  button.computeAllAttributes(buttonElement)
  return buttonElement.datum()
}

const round = (value: number, resolution: number) => {
  return resolution * Math.round(value / resolution)
}
